import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { rememberPostLoginPath } from '@/storage/post-login-redirect'
import { useAccountStore } from '@/stores/account-store'
import { PlanConstraintProvider } from '@/context/plan-constraint-context'
import { LoadingScreen } from '@/components/common/loading-screen'
import { Button } from '@/components/ui/button'
import { useGetUserQuery } from '@/services/api/users-api'
import { BETA, COGNITO } from '@/config/constants'
import {
  SUBSCRIBED_BILLING_PATH,
  UNSUBSCRIBED_BILLING_PATH,
  subscriptionState,
} from '../subscription-state'

const IS_MOCK_AUTH = !COGNITO.userPoolId

function complianceExpired(expiresAtSec: number): boolean {
  return !expiresAtSec || Math.floor(Date.now() / 1000) >= expiresAtSec
}

/**
 * Protects authenticated-only routes. Gating is derived from subscription status
 * (see subscription-state.ts):
 *   none      → /billing (subscribe)
 *   needs_fix → /account/billing (fix payment) — unless already under /account
 *   grace     → full access (account page shows the "action required" nudge)
 *   active    → full access + compliance gates
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const location = useLocation()
  const session = useAccountStore((s) => s.session)
  const isAuthenticating = useAccountStore((s) => s.isAuthenticating)

  // Fetch fresh user data from API (skip in mock auth mode or when not logged in)
  const {
    data: user,
    isLoading: userLoading,
    isError: userError,
    refetch: refetchUser,
  } = useGetUserQuery(undefined, {
    skip: !session || IS_MOCK_AUTH,
  })

  // Sync fetched user to store
  const setUser = useAccountStore((s) => s.setUser)
  useEffect(() => {
    if (user) setUser(user)
  }, [user, setUser])

  // No session → either we're still restoring (don't flash redirect) or
  // truly logged out (redirect to login). Once a session exists we never
  // block here — a re-authentication happening in the background must not
  // unmount the protected subtree.
  if (!session) {
    if (isAuthenticating) return <LoadingScreen />
    rememberPostLoginPath(location)
    return <Navigate to="/login" replace />
  }

  // Wait for user profile to load before making billing decision (production only)
  if (!IS_MOCK_AUTH && userLoading) {
    return <LoadingScreen />
  }

  // A failed profile request leaves nothing to decide access from, and the
  // gating below needs a user, so rendering on would open the whole app with
  // no subscription check. Hold here until a retry succeeds. A failed
  // background refetch keeps its last good user, so it never lands here.
  if (!IS_MOCK_AUTH && userError && !user) {
    return <AccountLoadError onRetry={() => void refetchUser()} />
  }

  // Subscription-based gating. Access IS the subscription — beta included: a beta user
  // holds a real $0 comp sub, so an active sub ⇒ access, exactly like the normal app.
  // The only beta differences: someone WITHOUT a live sub goes to /beta-locked (beta has
  // no /billing), and the free comp plan skips the compliance gates. Skipped entirely in
  // mock-auth mode and for `betaAccess` users (admin-code comps — access without a sub).
  if (!IS_MOCK_AUTH && user && !user.betaAccess) {
    // testClockTime (dev/test only) lets the grace window evaluate on the test-clock
    // timeline; undefined in prod → subscriptionState falls back to real time.
    const state = subscriptionState(user, user.testClockTime ?? undefined)

    // No live subscription. Beta has no billing → /beta-locked; otherwise → subscribe.
    if (state === 'none') {
      return <Navigate to={BETA ? '/beta-locked' : UNSUBSCRIBED_BILLING_PATH} replace />
    }

    // Payment lapsed past the grace window. Beta's $0 sub can't go past_due, but stay
    // safe: beta → /beta-locked; otherwise → the account page to fix payment (let them
    // stay once they're anywhere under /account).
    if (state === 'needs_fix') {
      if (BETA) return <Navigate to="/beta-locked" replace />
      if (!location.pathname.startsWith('/account')) {
        return <Navigate to={SUBSCRIBED_BILLING_PATH} replace />
      }
    }

    // Compliance gates apply only to active PAID subscribers. A free/comp plan (beta's
    // $0 pro_plus) carries no market-data billing, so the NASDAQ professional attestation
    // doesn't apply — skip it. /billing/* routes live outside this guard.
    if (state === 'active') {
      const activeSub = user.subscriptions?.find(
        (s) => s.subscriptionStatus === 'active' || s.subscriptionStatus === 'trialing',
      )
      const isFreePlan = activeSub?.planDetails?.amount === 0
      if (!isFreePlan) {
        // (1) 6-month re-certification: no survey on file, or it has expired.
        const expiresAt = user.complianceExpiresAt ? Number(user.complianceExpiresAt) : 0
        if (complianceExpired(expiresAt)) {
          return <Navigate to="/billing/recertify" replace />
        }

        // (2) Status flip: the latest survey disagrees with the subscription's
        // professional state → force the product switch. Both flags must be known
        // (older records may lack them — don't gate on missing data).
        if (
          typeof user.complianceProfessional === 'boolean' &&
          typeof activeSub?.professional === 'boolean' &&
          user.complianceProfessional !== activeSub.professional
        ) {
          return <Navigate to="/billing/switch" replace />
        }
      }
    }
  }

  // Session exists — render protected content.
  return <PlanConstraintProvider>{children}</PlanConstraintProvider>
}

function AccountLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-foreground">
          We couldn&apos;t load your account
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">Check your connection, then try again.</p>
        <Button className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  )
}
